import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";
import { toast } from "react-toastify";
import { Card, Badge, Form, InputGroup } from "react-bootstrap";
import { Button, Select } from "antd";
import { ReloadOutlined, CheckOutlined, DeleteOutlined, BellOutlined, InfoCircleOutlined, FileTextOutlined, WalletOutlined } from "@ant-design/icons";

const normaliseResponse = (response) => {
    if (!response?.success) {
        return [];
    }
    if (Array.isArray(response?.data)) {
        return response.data;
    }
    if (Array.isArray(response?.data?.items)) {
        return response.data.items;
    }
    return [];
};

const toNumericOrNull = (value) => {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const Notifications = () => {
    const { userData } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const companyId = useMemo(
        () => toNumericOrNull(userData?.company_id || userData?.companyId || userData?.company),
        [userData]
    );

    const agentId = useMemo(
        () => toNumericOrNull(userData?.agent_id || userData?.agentId),
        [userData]
    );

    useEffect(() => {
        fetchNotifications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.id, companyId, agentId]);

    const fetchNotifications = async () => {
        if (!userData?.id) {
            setNotifications([]);
            return;
        }

        try {
            setLoading(true);
            const conditions = {};
            
            // Priority 1: Filter by company_id if user belongs to a company
            if (companyId) {
                conditions.company_id = companyId;
            }
            // Priority 2: Filter by agent_id if user is an agent
            else if (agentId) {
                conditions.agent_id = agentId;
            }
            // Priority 3: Filter by user_id for individual users
            else {
                conditions.user_id = userData.id;
            }
            
            console.log("Fetching notifications with conditions:", conditions);
            
            const response = await oneKlicks.read(
                "",
                "insurance_notifications",
                conditions,
                "created_at DESC",
                "",
                "",
                "0,200"
            );

            const notificationRows = normaliseResponse(response);
            setNotifications(notificationRows || []);
        } catch (error) {
            console.error("Error fetching notifications:", error);
            toast.error("Failed to load notifications");
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredNotifications = useMemo(() => {
        let filtered = notifications;
        
        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter((notification) => {
                return (
                    notification.title?.toLowerCase().includes(searchLower) ||
                    notification.message?.toLowerCase().includes(searchLower) ||
                    notification.type?.toLowerCase().includes(searchLower)
                );
            });
        }
        
        if (statusFilter === "unread") {
            filtered = filtered.filter((notification) => !notification.read);
        } else if (statusFilter === "read") {
            filtered = filtered.filter((notification) => notification.read);
        }
        
        return filtered;
    }, [notifications, searchText, statusFilter]);

    const markAsRead = async (notificationId) => {
        try {
            const notification = notifications.find(n => n.id === notificationId);
            if (!notification) return;

            // Update in database if possible
            try {
                await oneKlicks.update(
                    "",
                    "insurance_notifications",
                    { read: true, read_at: new Date().toISOString() },
                    { id: notificationId }
                );
            } catch (error) {
                console.log("Could not update in database, updating locally only");
            }

            // Update local state
            setNotifications(prev => prev.map(n => 
                n.id === notificationId ? { ...n, read: true } : n
            ));
            toast.success("Notification marked as read");
        } catch (error) {
            console.error("Error marking notification as read:", error);
            toast.error("Failed to mark notification as read");
        }
    };

    const markAllAsRead = async () => {
        try {
            const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
            if (unreadIds.length === 0) {
                toast.info("All notifications are already read");
                return;
            }

            // Update all in database
            try {
                for (const id of unreadIds) {
                    await oneKlicks.update(
                        "",
                        "insurance_notifications",
                        { read: true, read_at: new Date().toISOString() },
                        { id }
                    );
                }
            } catch (error) {
                console.log("Could not update in database, updating locally only");
            }

            // Update local state
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            toast.success(`Marked ${unreadIds.length} notifications as read`);
        } catch (error) {
            console.error("Error marking all as read:", error);
            toast.error("Failed to mark all notifications as read");
        }
    };

    const deleteNotification = async (notificationId) => {
        try {
            // Delete from database if possible
            try {
                await oneKlicks.delete("", "insurance_notifications", { id: notificationId });
            } catch (error) {
                console.log("Could not delete from database, removing locally only");
            }

            // Update local state
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            toast.success("Notification deleted");
        } catch (error) {
            console.error("Error deleting notification:", error);
            toast.error("Failed to delete notification");
        }
    };

    const getNotificationIcon = (type) => {
        switch(type) {
            case 'policy_renewal':
                return <InfoCircleOutlined style={{ fontSize: '18px' }} />;
            case 'claim_status':
                return <FileTextOutlined style={{ fontSize: '18px' }} />;
            case 'payment_due':
                return <WalletOutlined style={{ fontSize: '18px' }} />;
            default:
                return <BellOutlined style={{ fontSize: '18px' }} />;
        }
    };

    const getNotificationColor = (type) => {
        switch(type) {
            case 'policy_renewal':
                return { bg: '#e3f2fd', icon: '#2196f3', badge: 'info' };
            case 'claim_status':
                return { bg: '#fff3e0', icon: '#ff9800', badge: 'warning' };
            case 'payment_due':
                return { bg: '#e8f5e9', icon: '#4caf50', badge: 'success' };
            default:
                return { bg: '#f3e5f5', icon: '#9c27b0', badge: 'secondary' };
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return "Just now";
        if (diffMins < 60) return `${diffMins} min ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <div className="subbody" style={{ width: "100%", padding: "2% 4%" }}>
            <div className="dc-haslayout dc-dbsectionspace">
                <div className="container-fluid py-3">
                    {/* Header */}
                    <div className="card mb-5" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">Notifications Management</h5>
                                    <Badge bg="info" style={{ borderRadius: "5px" }}>Notifications</Badge>
                                    {unreadCount > 0 && (
                                        <Badge bg="danger" style={{ borderRadius: "5px" }}>
                                            {unreadCount} New
                                        </Badge>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    {unreadCount > 0 && (
                                        <Button 
                                            className="btn-outline-success" 
                                            onClick={markAllAsRead}
                                            disabled={loading}
                                            style={{ height: '40px', borderRadius: "5px" }}
                                        >
                                            <CheckOutlined /> Mark All Read
                                        </Button>
                                    )}
                                    <Button 
                                        className="btn-outline-primary" 
                                        onClick={fetchNotifications}
                                        disabled={loading}
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    >
                                        <ReloadOutlined /> Refresh
                                    </Button>
                                </div>
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-3">
                                <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Search
                                    </label>
                                    <InputGroup>
                                        <Form.Control
                                            placeholder="Search notifications..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            style={{ height: '40px', borderRadius: "5px" }}
                                        />
                                    </InputGroup>
                                </div>
                                <div style={{ minWidth: '180px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Status
                                    </label>
                                    <Select
                                        value={statusFilter}
                                        onChange={(value) => setStatusFilter(value)}
                                        style={{ width: '100%' }}
                                        size="large"
                                        options={[
                                            { value: 'all', label: 'All Notifications' },
                                            { value: 'unread', label: 'Unread' },
                                            { value: 'read', label: 'Read' },
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                            </div>

                    {/* Notifications Table */}
                    <div className="card" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            {loading ? (
                                <div className="text-center p-5">
                                    <div className="spinner-border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : filteredNotifications.length > 0 ? (
                                <div className="table-responsive">
                                    <table hover size="sm" className="mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '50px' }}>Icon</th>
                                                <th>Type</th>
                                                <th>Title</th>
                                                <th>Message</th>
                                                <th>Date</th>
                                                <th>Status</th>
                                                <th style={{ width: '150px' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredNotifications.map((notification) => {
                                        const colors = getNotificationColor(notification.type);
                                        return (
                                                    <tr 
                                                key={notification.id}
                                                style={{ 
                                                            backgroundColor: notification.read ? '#fff' : '#f8f9fa',
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => !notification.read && markAsRead(notification.id)}
                                                    >
                                                        <td data-label="Icon">
                                                        <div style={{ 
                                                                width: '40px', 
                                                                height: '40px', 
                                                            borderRadius: '50%', 
                                                            backgroundColor: colors.bg, 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'center', 
                                                                color: colors.icon
                                                        }}>
                                                                {getNotificationIcon(notification.type)}
                                                        </div>
                                                        </td>
                                                        <td data-label="Type">
                                                            <Badge bg={colors.badge} style={{ borderRadius: "5px", textTransform: 'capitalize' }}>
                                                                {notification.type?.replace('_', ' ') || 'General'}
                                                            </Badge>
                                                        </td>
                                                        <td data-label="Title">
                                                            <div style={{ fontWeight: notification.read ? '400' : '600', color: '#333' }}>
                                                                    {notification.title || "Notification"}
                                                            </div>
                                                        </td>
                                                        <td data-label="Message">
                                                            <div style={{ fontSize: '13px', color: '#666', maxWidth: '400px' }}>
                                                                {notification.message || "No message"}
                                                            </div>
                                                        </td>
                                                        <td data-label="Date" style={{ fontSize: '13px', color: '#666' }}>
                                                            {formatDate(notification.created_at)}
                                                        </td>
                                                        <td data-label="Status">
                                                            {notification.read ? (
                                                                <Badge bg="secondary" style={{ borderRadius: "5px" }}>Read</Badge>
                                                            ) : (
                                                                <Badge bg="danger" style={{ borderRadius: "5px" }}>Unread</Badge>
                                                            )}
                                                        </td>
                                                        <td data-label="Action">
                                                            <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                                                                {!notification.read && (
                                                                    <Button 
                                                                        href="#" 
                                                                        className="dc-update-servicesss fit-content btn-success" 
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            markAsRead(notification.id);
                                                                        }}
                                                                        style={{ borderRadius: "5px" }}
                                                                    >
                                                                        <CheckOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                                        Mark Read
                                                                    </Button>
                                                                )}
                                                                <Button 
                                                                    href="#" 
                                                                    className="dc-update-servicesss fit-content btn-danger" 
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        deleteNotification(notification.id);
                                                                    }}
                                                                    style={{ borderRadius: "5px" }}
                                                                >
                                                                    <DeleteOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                                    Delete
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                        );
                                    })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="dc-emptydata-holder">
                                    <div className="dc-emptydata">
                                        <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                            <span></span>
                                            <em>No notifications available</em>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Notifications;
